import { AssistantsEndpoints, assistantsFetch } from './assistantsApiClient';

/**
 * @param {Record<string, unknown> | null | undefined} payload
 * @param {string} currentAgentSlug
 */
export function isCuratedAssistantPayload(payload, currentAgentSlug) {
  return Boolean(
    payload?.scope_type
    || currentAgentSlug.includes('state_laws')
    || currentAgentSlug.includes('doi_researcher'),
  );
}

/**
 * @param {{
 *   payload: Record<string, unknown>;
 *   currentAgentSlug: string;
 *   question: string;
 *   previousResponseId?: string | null;
 * }} params
 * @returns {{ endpoint: string, body: Record<string, unknown> }}
 */
export function buildChatStreamRequest({
  payload,
  currentAgentSlug,
  question,
  previousResponseId = null,
}) {
  if (isCuratedAssistantPayload(payload, currentAgentSlug)) {
    return {
      endpoint: AssistantsEndpoints.CURATED_CHAT_STREAM,
      body: {
        agent_slug: currentAgentSlug,
        question,
        previous_response_id: previousResponseId,
        scope: {
          scope_type: payload.scope_type || 'state',
          scope_key: payload.scope_key,
          jurisdiction_name: payload.jurisdiction_name,
        },
      },
    };
  }

  return {
    endpoint: AssistantsEndpoints.CHAT_STREAM,
    body: {
      competitors: payload.competitors || [],
      is_custom_url: Boolean(payload.is_custom_url),
      user_preferred_urls: payload.user_preferred_urls || [],
      question,
      previous_response_id: previousResponseId,
      agent_slug: currentAgentSlug,
    },
  };
}

/**
 * Stream assistant chat SSE events.
 * @param {{
 *   endpoint: string;
 *   body: Record<string, unknown>;
 *   signal?: AbortSignal;
 *   onEvent: (event: Record<string, unknown>) => void | Promise<void>;
 * }} params
 */
export async function streamAssistantChat({ endpoint, body, signal, onEvent }) {
  const response = await assistantsFetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    let detail = `Request failed (${response.status})`;
    try {
      const json = await response.json();
      detail = json?.detail || detail;
    } catch {
      const text = await response.text();
      if (text) detail = text.slice(0, 200);
    }
    throw new Error(detail);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const rawLine of lines) {
      if (!rawLine.startsWith('data:')) continue;
      const raw = rawLine.slice(5).trim();
      if (!raw) continue;
      try {
        const event = JSON.parse(raw);
        await onEvent(event);
      } catch (parseErr) {
        if (parseErr instanceof SyntaxError) continue;
        throw parseErr;
      }
    }
  }
}
